import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

// REGERA conceptual sequence:
// LEADERSHIP (Direction) + CREATIVITY (Possibility) + INNOVATION (Realisation)
// -> TRANSFORMATION -> METAMORPHOSIS
export const PHASES = [
  { key: "complexity", label: "ENTERPRISE", label2: "COMPLEXITY", sub: "Movement without alignment", dur: 1900 },
  { key: "leadership", label: "LEADERSHIP", sub: "Direction", dur: 1700 },
  { key: "creativity", label: "CREATIVITY", sub: "Possibility", dur: 1700 },
  { key: "innovation", label: "INNOVATION", sub: "Realisation", dur: 1700 },
  { key: "transformation", label: "TRANSFORMATION", sub: "Strategy · People · Process · Technology", dur: 2000 },
  { key: "metamorphosis", label: "METAMORPHOSIS", sub: "Adaptive · Intelligent · Integrated", dur: 2100 },
  { key: "final", label: "METAMORPHOSIS", sub: "REGERA · Leading Transformation …", dur: 1500 },
];
const TOTAL = PHASES.reduce((a, p) => a + p.dur, 0);
const GRAPHITE = [140, 147, 152];
const RED = [230, 0, 0];

function seedRand(i) {
  const x = Math.sin(i * 999.13) * 43758.5453;
  return x - Math.floor(x);
}

function layout(kind, i, N, w, h) {
  const cx = w * 0.52;
  const cy = h * 0.5;
  const R = Math.min(w, h) * 0.44;
  if (kind === "scatter") {
    return { x: seedRand(i) * w, y: seedRand(i + 99) * h };
  }
  if (kind === "streams") {
    const lanes = Math.max(4, Math.round(N / 8));
    const lane = i % lanes;
    const per = Math.ceil(N / lanes);
    const idx = Math.floor(i / lanes);
    return {
      x: w * 0.12 + (idx / Math.max(1, per - 1)) * w * 0.78,
      y: h * (0.18 + (lane / Math.max(1, lanes - 1)) * 0.64),
    };
  }
  if (kind === "network") {
    const a = (i / N) * Math.PI * 2 * 3;
    const ringR = R * (0.35 + 0.6 * ((i % 3) / 2));
    return { x: cx + ringR * Math.cos(a), y: cy + ringR * Math.sin(a) * 0.92 };
  }
  // organic — phyllotaxis spiral (living intelligent form)
  const golden = Math.PI * (3 - Math.sqrt(5));
  const t = i / N;
  const r = Math.sqrt(t) * R * 1.02;
  const a = i * golden;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

const PHASE_KIND = {
  complexity: "scatter",
  leadership: "streams",
  creativity: "network",
  innovation: "network",
  transformation: "network",
  metamorphosis: "organic",
  final: "organic",
};
const RED_FRACTION = {
  complexity: 0, leadership: 0.12, creativity: 0.22, innovation: 0.34,
  transformation: 0.55, metamorphosis: 0.4, final: 0.34,
};
const EDGE = {
  complexity: 0.1, leadership: 0.22, creativity: 0.32, innovation: 0.5,
  transformation: 0.7, metamorphosis: 0.55, final: 0.5,
};

function lerp(a, b, t) { return a + (b - a) * t; }
function mixColor(c1, c2, t) {
  return `rgb(${Math.round(lerp(c1[0], c2[0], t))},${Math.round(lerp(c1[1], c2[1], t))},${Math.round(lerp(c1[2], c2[2], t))})`;
}

export default function HeroSequence({ mobile = false }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const [phaseIdx, setPhaseIdx] = useState(0);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext("2d");
    const N = mobile ? 30 : 62;
    let w = 0, h = 0, dpr = Math.min(window.devicePixelRatio || 1, 2);

    const nodes = Array.from({ length: N }, (_, i) => ({
      x: seedRand(i) * 500, y: seedRand(i + 7) * 400, tx: 0, ty: 0,
      rad: mobile ? 1.6 : 2.2 + seedRand(i + 3) * 1.6, ph: seedRand(i + 5) * Math.PI * 2,
    }));

    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      w = rect.width; h = rect.height;
      canvas.width = w * dpr; canvas.height = h * dpr;
      canvas.style.width = w + "px"; canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);

    const phaseAt = (elapsed) => {
      let t = elapsed % TOTAL, i = 0;
      for (; i < PHASES.length; i++) {
        if (t < PHASES[i].dur) return { i, p: PHASES[i], prog: t / PHASES[i].dur };
        t -= PHASES[i].dur;
      }
      return { i: PHASES.length - 1, p: PHASES[PHASES.length - 1], prog: 1 };
    };

    const drawFrame = (elapsed) => {
      const { i, p, prog } = phaseAt(elapsed);
      if (i !== lastPhase.current) { lastPhase.current = i; setPhaseIdx(i); }
      const kind = PHASE_KIND[p.key];
      const redFrac = RED_FRACTION[p.key];
      const edgeAlpha = EDGE[p.key];

      // assign targets + ease
      for (let n = 0; n < N; n++) {
        const t = layout(kind, n, N, w, h);
        nodes[n].tx = t.x; nodes[n].ty = t.y;
        const ease = kind === "scatter" ? 0.045 : 0.06;
        nodes[n].x += (nodes[n].tx - nodes[n].x) * ease;
        nodes[n].y += (nodes[n].ty - nodes[n].y) * ease;
      }

      ctx.clearRect(0, 0, w, h);

      // transformation radial pulse
      if (p.key === "transformation") {
        const pr = prog * Math.min(w, h) * 0.75;
        const g = ctx.createRadialGradient(w * 0.52, h * 0.5, 0, w * 0.52, h * 0.5, pr + 1);
        g.addColorStop(0, `rgba(230,0,0,${0.18 * (1 - prog)})`);
        g.addColorStop(1, "rgba(230,0,0,0)");
        ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
      }
      // metamorphosis core glow
      if (p.key === "metamorphosis" || p.key === "final") {
        const g = ctx.createRadialGradient(w * 0.52, h * 0.5, 0, w * 0.52, h * 0.5, Math.min(w, h) * 0.5);
        g.addColorStop(0, "rgba(230,0,0,0.10)");
        g.addColorStop(1, "rgba(230,0,0,0)");
        ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
      }

      // edges
      const thresh = (mobile ? 90 : 130) * (0.7 + edgeAlpha);
      const pulse = p.key === "metamorphosis" ? 0.6 + 0.4 * Math.sin(elapsed / 260) : 1;
      for (let a = 0; a < N; a++) {
        for (let b = a + 1; b < N; b++) {
          const dx = nodes[a].x - nodes[b].x, dy = nodes[a].y - nodes[b].y;
          const d = Math.hypot(dx, dy);
          if (d < thresh) {
            const al = (1 - d / thresh) * edgeAlpha * 0.7 * pulse;
            const red = a / N < redFrac || b / N < redFrac;
            ctx.strokeStyle = red ? `rgba(230,0,0,${al})` : `rgba(150,157,162,${al * 0.7})`;
            ctx.lineWidth = red ? 1 : 0.6;
            ctx.beginPath(); ctx.moveTo(nodes[a].x, nodes[a].y); ctx.lineTo(nodes[b].x, nodes[b].y); ctx.stroke();
          }
        }
      }

      // leadership directional beam
      if (p.key === "leadership") {
        const bx = w * (0.05 + prog * 0.9);
        const g = ctx.createLinearGradient(bx - 40, 0, bx + 40, 0);
        g.addColorStop(0, "rgba(230,0,0,0)");
        g.addColorStop(0.5, "rgba(230,0,0,0.5)");
        g.addColorStop(1, "rgba(230,0,0,0)");
        ctx.fillStyle = g; ctx.fillRect(bx - 40, 0, 80, h);
      }

      // nodes
      for (let n = 0; n < N; n++) {
        const red = n / N < redFrac;
        const breathe = p.key === "metamorphosis" || p.key === "final"
          ? 1 + 0.25 * Math.sin(elapsed / 300 + nodes[n].ph) : 1;
        const rr = nodes[n].rad * breathe;
        if (red) {
          ctx.shadowColor = "rgba(230,0,0,0.9)"; ctx.shadowBlur = 8;
          ctx.fillStyle = mixColor(GRAPHITE, RED, 1);
        } else {
          ctx.shadowBlur = 0;
          ctx.fillStyle = `rgba(150,157,162,${p.key === "complexity" ? 0.6 : 0.85})`;
        }
        ctx.beginPath(); ctx.arc(nodes[n].x, nodes[n].y, rr, 0, Math.PI * 2); ctx.fill();
      }
      ctx.shadowBlur = 0;
    };

    const lastPhase = { current: -1 };
    let raf, start = performance.now();

    if (reduce) {
      const STATIC = PHASES.slice(0, 5).reduce((a, p) => a + p.dur, 0) + 800;
      const paint = () => {
        for (let n = 0; n < N; n++) {
          const t = layout("organic", n, N, w || 500, h || 400);
          nodes[n].x = t.x; nodes[n].y = t.y;
        }
        drawFrame(STATIC);
        setPhaseIdx(5);
      };
      const ro2 = new ResizeObserver(() => paint());
      ro2.observe(wrap);
      requestAnimationFrame(() => requestAnimationFrame(paint));
      return () => { ro.disconnect(); ro2.disconnect(); };
    }

    const loop = (now) => {
      drawFrame(now - start);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [mobile]);

  const phase = PHASES[phaseIdx];

  return (
    <div ref={wrapRef} className="relative h-full w-full" data-testid="hero-sequence">
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" aria-hidden="true" />

      {/* Phase caption — bottom aligned, does not fight the headline */}
      <div className="pointer-events-none absolute bottom-16 right-6 lg:bottom-20 lg:right-10 text-right max-w-[78%]">
        <AnimatePresence mode="wait">
          <motion.div
            key={phaseIdx}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center justify-end gap-2 mb-1.5">
              <span className="h-px w-6 bg-[#e60000]" />
              <span className="text-[10px] tracking-[0.28em] text-white/50 uppercase">
                {phase.key === "final" ? "The REGERA outcome" : `0${phaseIdx + 1} / 07`}
              </span>
            </div>
            <p className="font-display font-black tracking-tight leading-[0.9] text-white text-2xl sm:text-3xl lg:text-4xl">
              {phase.label}{phase.label2 ? <><br />{phase.label2}</> : null}
            </p>
            <p className="mt-1.5 text-[11px] sm:text-xs tracking-[0.1em] text-[#e60000] uppercase">
              {phase.sub}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Conceptual model rail (desktop) */}
      <div className="pointer-events-none absolute top-24 right-6 hidden lg:flex flex-col items-end gap-1.5 text-[10px] tracking-[0.2em] uppercase">
        {["Leadership", "Creativity", "Innovation"].map((w, i) => (
          <span
            key={w}
            className={`transition-colors duration-500 ${
              phaseIdx === i + 1 ? "text-[#e60000]" : phaseIdx > i + 1 ? "text-white/70" : "text-white/25"
            }`}
          >
            {w}
          </span>
        ))}
      </div>
    </div>
  );
}
